import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { AthleteItem, UserProfile } from '../services/api';
import { getPlatformStore } from '../platform';
import { pickDefaultAthleteId } from '../utils/pickDefaultAthleteId';

interface AthleteContextValue {
  isCoach: boolean;
  athletes: AthleteItem[];
  selectedAthleteId: number | null;
  effectiveUserId: number | null;
  coachMenuLocked: boolean;
  selectAthlete: (id: number | null) => void;
}

// Providers can remain mounted while Vite refreshes consumers of this module.
// Keep their context identity together across those development-only updates.
const AthleteContext =
  (import.meta.hot?.data.athleteContext as React.Context<AthleteContextValue | null> | undefined)
  ?? createContext<AthleteContextValue | null>(null);
if (import.meta.hot) import.meta.hot.data.athleteContext = AthleteContext;

export function AthleteProvider({
  user,
  children,
}: {
  user: UserProfile | null;
  children: React.ReactNode;
}) {
  const isCoach = user?.role === 'coach';
  const athletes = useMemo(() => user?.athletes ?? [], [user?.athletes]);
  const [selectedAthleteId, setSelectedAthleteId] = useState<number | null>(null);
  const selectionUserIdRef = useRef<number | null>(null);
  const athleteSelectionIsManualRef = useRef(false);
  const [selectionRestoredForUser, setSelectionRestoredForUser] = useState<number | null>(null);
  const selectionAthletesRef = useRef(athletes);
  selectionAthletesRef.current = athletes;

  // Restore before persisting: the initial null/default must not overwrite disk.
  useEffect(() => {
    const viewerId = user?.id;
    if (!isCoach || viewerId == null) return;
    let cancelled = false;
    const restore = async () => {
      let savedId: number | null = null;
      try {
        savedId = (await getPlatformStore().getSelectedAthleteId?.()) ?? null;
      } catch {
        // Unavailable storage still permits a valid default selection.
      }
      if (cancelled) return;
      const manual = athleteSelectionIsManualRef.current;
      athleteSelectionIsManualRef.current = true;
      setSelectedAthleteId((currentId) => {
        const candidate = manual ? currentId : savedId;
        const available = selectionAthletesRef.current;
        return pickDefaultAthleteId(available, candidate);
      });
      setSelectionRestoredForUser(viewerId);
    };
    void restore();
    return () => { cancelled = true; };
  }, [user?.id, isCoach]);

  useLayoutEffect(() => {
    const viewerId = user?.id ?? null;
    const viewerChanged = selectionUserIdRef.current !== viewerId;
    selectionUserIdRef.current = viewerId;
    if (viewerChanged) {
      athleteSelectionIsManualRef.current = false;
      setSelectionRestoredForUser(null);
    }

    setSelectedAthleteId((currentId) => {
      if (!isCoach || selectionRestoredForUser !== user?.id) return null;
      if (athleteSelectionIsManualRef.current && currentId != null && athletes.some((athlete) => athlete.id === currentId)) {
        return currentId;
      }
      return pickDefaultAthleteId(athletes);
    });
  }, [user?.id, isCoach, athletes, selectionRestoredForUser]);

  useEffect(() => {
    if (!isCoach || selectionRestoredForUser !== user?.id) return;
    void getPlatformStore().setSelectedAthleteId(selectedAthleteId);
  }, [selectedAthleteId, isCoach, user?.id, selectionRestoredForUser]);

  const selectAthlete = useCallback((id: number | null) => {
    athleteSelectionIsManualRef.current = true;
    setSelectedAthleteId(id);
  }, []);

  const coachMenuLocked = isCoach && athletes.length === 0;
  const effectiveUserId = isCoach ? selectedAthleteId : (user?.id ?? null);

  const value = useMemo(
    () => ({
      isCoach,
      athletes,
      selectedAthleteId,
      effectiveUserId,
      coachMenuLocked,
      selectAthlete,
    }),
    [isCoach, athletes, selectedAthleteId, effectiveUserId, coachMenuLocked, selectAthlete],
  );

  return <AthleteContext.Provider value={value}>{children}</AthleteContext.Provider>;
}

export function useAthleteContext(): AthleteContextValue {
  const ctx = useContext(AthleteContext);
  if (!ctx) throw new Error('useAthleteContext must be used within AthleteProvider');
  return ctx;
}
